import React, { useMemo } from 'react';

import clsx from 'clsx';

import { Container } from './Container';

import styles from './SlideInput.module.scss';

type InputProps = {
  className?: string;
  labelText: React.ReactNode;
  supportingText?: React.ReactNode;
  singularNoun?: string;
  pluralCollectiveNoun?: string;
  hideLabel?: boolean;
  compact?: boolean;
  name?: string;
  from?: number;
  to?: number;
  interval?: number;
  value?: number;
  onChange?: (value: number) => void;
  toLabel?: string | number;
};

export const SlideInput = ({
  className,
  labelText,
  supportingText,
  singularNoun,
  pluralCollectiveNoun,
  hideLabel = false,
  compact = false,
  name = '',
  from = 0,
  to = 100,
  interval = 1,
  value = from,
  toLabel,
  onChange,
}: InputProps) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(event.target.value);
    if (onChange && newValue >= from && newValue <= to) {
      onChange(newValue);
    }
  };

  const displayValue = useMemo(() => {
    const isAtMax = value === to;
    const formattedValue = isAtMax && toLabel ? toLabel : value;

    return `${singularNoun ? singularNoun + ' ' : ''}${formattedValue}${
      pluralCollectiveNoun ? ' ' + pluralCollectiveNoun : ''
    }`;
  }, [value, to, toLabel, singularNoun, pluralCollectiveNoun]);

  // Position tooltip above slider thumb
  const valuePercent = useMemo(() => {
    const percent = ((value - from) / (to - from)) * 100; // Slider value as percentage (0-100%)
    const thumbOffset = 0.9 - (percent / 100) * 1.8; // Adjust for thumb width (1.8rem)
    return `calc(${percent}% + ${thumbOffset}rem)`;
  }, [value, from, to]);

  return (
    <Container
      className={clsx(
        {
          [styles.compact]: compact,
        },
        className,
      )}
      labelText={labelText}
      hideLabel={hideLabel}
      supportingText={supportingText}
    >
      <div className={styles['slide-input']}>
        <div className={clsx(styles['input-wrapper'])}>
          <span>{from}</span>
          <div
            className={clsx(styles['range-wrapper'])}
            data-value={displayValue}
            style={{ ['--value-percent' as any]: valuePercent }}
          >
            <input
              type="range"
              name={name}
              className="flex-1"
              min={from}
              max={to}
              step={interval}
              value={value}
              onChange={handleChange}
            />
          </div>
          <span>{toLabel ?? to}</span>
        </div>
      </div>
    </Container>
  );
};
